import { Command as CommandPrimitive } from "cmdk";
import { Check } from "lucide-react";
import { useRef, useState } from "react";
import { usePopper } from "react-popper";
import { useClickAway } from "react-use";

import { useControllableState } from "@/hooks/use-controllable-state";
import { cn } from "@/lib/utils";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./ui/command";
import { Input } from "./ui/input";
import { Spinner } from "./ui/spinner";

type Props<T extends string> = {
  value?: string;
  onChange: (value: string) => void;
  items: { value: T; label: string }[] | undefined;
  onSelect: (value: T) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  placeholder?: string;
};

export function AutoComplete<T extends string>({
  value: valueProp,
  onChange: onChangeProp,
  items,
  onSelect,
  isLoading,
  emptyMessage = "No items.",
  placeholder = "Search",
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useControllableState({
    value: valueProp,
    onChange: onChangeProp,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const referenceRef = useRef<HTMLDivElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const { styles, attributes } = usePopper(
    referenceRef.current,
    popperRef.current,
    {
      placement: "auto-start",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 4],
          },
        },
      ],
    },
  );

  useClickAway(containerRef, () => {
    setOpen(false);
  });

  const onSelectItem = (inputValue: string) => {
    setOpen(false);
    onSelect(inputValue as T);
  };

  return (
    <div className="flex items-center w-full relative" ref={containerRef}>
      <Command className="rounded-r-none" shouldFilter={false}>
        <div ref={referenceRef}>
          <CommandPrimitive.Input
            asChild
            value={value}
            onValueChange={(value) => {
              setValue(value);
              setOpen(!!value);
            }}
            onKeyDown={(e) => setOpen(e.key !== "Escape")}
            onClick={() => {
              setOpen(!!value);
            }}
          >
            <Input
              placeholder={placeholder}
              className="rounded-r-none border-r-0"
            />
          </CommandPrimitive.Input>
        </div>
        {isLoading && (
          <div className="absolute right-3 top-1/2 mt-0.5 -translate-y-1/2">
            <Spinner className="h-4 w-4" />
          </div>
        )}
        {!open && <CommandList aria-hidden="true" className="hidden" />}
        <div
          ref={popperRef}
          style={styles.popper as React.CSSProperties}
          {...attributes.popper}
          className={cn(
            "w-full z-50 bg-popover border rounded-md shadow-md",
            "transition-all duration-200 ease-in-out",
            open && typeof items !== "undefined"
              ? "opacity-100 translate-y-0 scale-100"
              : "opacity-0 translate-y-2 scale-95 pointer-events-none sr-only",
          )}
        >
          <CommandList>
            {isLoading && (
              <CommandPrimitive.Loading>
                <div className="p-2 flex justify-center items-center">
                  <Spinner className="w-5 h-5" />
                </div>
              </CommandPrimitive.Loading>
            )}
            {items && items.length > 0 && !isLoading ? (
              <CommandGroup>
                {items.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onMouseDown={(e) => e.preventDefault()}
                    onSelect={onSelectItem}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {!isLoading ? (
              <CommandEmpty>{emptyMessage ?? "No items."}</CommandEmpty>
            ) : null}
          </CommandList>
        </div>
      </Command>
    </div>
  );
}
